import { marked } from 'marked';
import DOMPurify from 'dompurify';
import * as he from 'he';

/**
 * Convert markdown to safe HTML
 * Supports: **bold**, *italic*, `code`, [links](url), lists, etc.
 * Handles HTML entity decoding to fix &lt; &gt; issues
 */
export const markdownToHtml = (markdown: string): string => {
  if (!markdown) return '';
  
  // Step 1: Decode HTML entities FIRST (fixes &lt;strong&gt; → <strong>)
  const decoded = he.decode(markdown);
  
  // Configure marked for inline rendering
  marked.setOptions({
    breaks: true,      // Support line breaks
    gfm: true,         // GitHub Flavored Markdown
  });
  
  // Step 2: Convert markdown to HTML (use parseInline for synchronous parsing)
  const html = marked.parse(decoded, { async: false }) as string;
  
  // Sanitize to prevent XSS attacks
  const safeHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'strong', 'em', 'u', 'code', 'pre', 'a', 'ul', 'ol', 'li', 
      'p', 'br', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'id']
  });
  
  // Make external links safe
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = safeHtml;
  tempDiv.querySelectorAll('a').forEach(link => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
  
  return tempDiv.innerHTML;
};
